import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useSyncScroll from "./hooks/useSyncScroll";
// import Pagination from "./components/Pagination";
import "./style.css";
import TableHeader from "./components/TableHeader";
import TableBody from "./components/TableBody";
import Pagination from "./components/Pagination";
import { getNumberOfPages, getPlaceholder } from "./utils";
import { BlkBoxTableProps, Column } from "../../types";

const BlkBoxTable = (props: BlkBoxTableProps) => {
  const {
    columns,
    isColumnDraggable = false,
    isRowDraggable = false,
    isPaginatorVisible = false,
    isRowExpandable = false,
    onRowExpanded,
    rowKey = "id",
    tableContainerClassName = "",
    isLoading = false,
    isRowSelectable = false,
    expansionArrowIcon,
    emptyStateComponent,
    virtualizationSettings = {},
    defaultRowsPerPage = 5,
    rowsPerPageOptions = [5, 10, 15],
    tableRef,
    filterText = "",
  } = props;

  const { bodyRef, contentRef, stickyCols } = useSyncScroll(props.rows);

  const rows = useMemo(
    () =>
      props.rows?.map((row) => {
        if (row._level === undefined) {
          row._level = 0;
        }
        return row;
      }),
    [props.rows]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [showRowPerPage, setShowPerPage] = useState(defaultRowsPerPage);
  const [resizing, setResizing] = useState<{
    isActive: boolean;
    index: number;
    initialX: number;
    currentWidth: number;
    staringPoint: {
      x: number;
      y: number;
    };
    height: number;
    type: "left" | "right" | "main";
  } | null>(null);

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const selectionColumn = {
    field: "selection",
    headerName: "select",
    width: 50,
  };

  const [sortingColumns, setSortingColumns] = useState(
    columns
      .filter((col) => col.sorting)
      .map((col) => ({ ...col, sortType: "default" }))
  );

  const [nonStickyColumns, setNonStickyColumns] = useState(
    columns
      .filter((col) => !col.sticky)
      .map((col) => ({ ...col, width: col?.width ?? 200 }))
  );

  const [leftStickyColumns, setLeftStickyColumns] = useState<Column[]>([
    ...columns
      .filter((col) => col.sticky === "left")
      .map((col) => ({ ...col, width: col?.width ?? 200 })),
  ]);

  const [rightStickyColumns, setRightStickyColumns] = useState(
    columns
      .filter((col) => col.sticky === "right")
      .map((col) => ({ ...col, width: col?.width ?? 200 }))
  );
  // Selection States

  const leftMargin = useMemo(
    () =>
      leftStickyColumns.reduce((acc, col) => acc + (col.width ?? 200) - 1, 0),
    [leftStickyColumns]
  );

  const rightMargin = useMemo(
    () =>
      rightStickyColumns.reduce((acc, col) => acc + (col.width ?? 200) - 1, 0),
    [rightStickyColumns]
  );

  const selectedRowsIds = useRef<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showIntermediateStateOnCheckbox, setShowIntermediateStateOnCheckbox] =
    useState(false);
  const [tableRows, setTableRows] = useState(rows);

  const handleChangePage = (page: React.SetStateAction<number>) => {
    setCurrentPage(page);
  };

  const handlePerChange = (value: string | number) => {
    setShowPerPage(+value);
  };

  // Selection Variables
  const topLevelRows = useMemo(
    () => rows.filter((row) => row._level === 0),
    [rows]
  );

  // Selection Handler Functions
  const selectAll = () => {
    const selectedRows = topLevelRows.map((row) => row[rowKey] as string);
    selectedRowsIds.current = new Set(selectedRows);
    setShowIntermediateStateOnCheckbox(false);
    setSelectedRows(new Set(selectedRowsIds.current));
  };

  const deselectAll = () => {
    selectedRowsIds.current.clear();
    setShowIntermediateStateOnCheckbox(false);
    setSelectedRows(new Set(selectedRowsIds.current));
  };

  const selectRow = (id: string) => {
    selectedRowsIds.current.add(id);
    // If all rows are selected, remove intermediate state of the checkbox else show it
    if (selectedRowsIds.current.size === topLevelRows.length) {
      setShowIntermediateStateOnCheckbox(false);
    } else {
      setShowIntermediateStateOnCheckbox(true);
    }
    setSelectedRows(new Set(selectedRowsIds.current));
  };

  const deselectRow = (id: string) => {
    selectedRowsIds.current.delete(id);
    if (selectedRowsIds.current.size === 0) {
      setShowIntermediateStateOnCheckbox(false);
    } else if (selectedRowsIds.current.size < topLevelRows.length) {
      setShowIntermediateStateOnCheckbox(true);
    }
    setSelectedRows(new Set(selectedRowsIds.current));
  };

  // Handlers to be exposed to the parent
  const getSelectedData = () => {
    return rows.filter(
      (row) =>
        row._level === 0 && selectedRowsIds.current.has(row[rowKey] as string)
    );
  };

  const getSelectedRowIds = () => {
    return Array.from(selectedRowsIds.current);
  };

  const handleSearch = useCallback((data: any[], searchText: string) => {
    if (!searchText) return data;
    const lowerCaseSearch = searchText.toLowerCase();
    const newFilteredRows = data.filter((row) => {
      return Object.values(row).some((value) =>
        String(value).toLowerCase().includes(lowerCaseSearch)
      );
    });
    return newFilteredRows;
  }, []);

  // Expose only handlers via ref
  useImperativeHandle(tableRef, () => ({
    getSelectedData,
    getSelectedRowIds,
  }));

  const handleMouseDown = (
    index: number,
    e: React.MouseEvent<HTMLDivElement>,
    type: "left" | "main" | "right"
  ) => {
    const headerCell = e.currentTarget.parentElement as HTMLDivElement;

    if (headerCell) {
      const headerCellDimensions = headerCell.getBoundingClientRect();
      const initialX = headerCellDimensions.right;
      const currentWidth = headerCell.offsetWidth;

      setResizing({
        isActive: true,
        index,
        initialX,
        currentWidth,
        type,
        staringPoint: {
          x: headerCellDimensions.left,
          y: headerCellDimensions.top,
        },
        height: headerCellDimensions.height,
      });

      if (overlayRef.current) {
        overlayRef.current.style.width = `${currentWidth}px`;
        overlayRef.current.style.left = `${headerCellDimensions.left}px`;
        overlayRef.current.style.top = `${headerCellDimensions.top}px`;
        overlayRef.current.style.height = `${headerCellDimensions.height}px`;
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (resizing?.isActive && overlayRef.current) {
      const deltaX = e.clientX - resizing.initialX;
      let newWidth = resizing.currentWidth + deltaX;
      if (newWidth < 100) {
        newWidth = 100;
      }
      overlayRef.current.style.width = `${newWidth}px`;
      overlayRef.current.style.left = `${resizing.staringPoint.x}px`;
      overlayRef.current.style.top = `${resizing.staringPoint.y}px`;
      overlayRef.current.style.height = `${resizing.height}px`;
    }
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (resizing?.isActive) {
      const setter =
        resizing.type === "left"
          ? setLeftStickyColumns
          : resizing.type === "right"
          ? setRightStickyColumns
          : setNonStickyColumns;

      const deltaX = e.clientX - resizing?.initialX;
      const newWidth = resizing.currentWidth + deltaX;
      setter((prev: any) =>
        prev.map((col: Column, i: number) =>
          i === resizing.index
            ? { ...col, width: newWidth }
            : { ...col, width: col.width }
        )
      );
      setResizing(null);
    }
  };

  useEffect(() => {
    if (resizing?.isActive) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [draggedRow, setDraggedRow] = useState<number | null>(null);

  // Column Drag Handlers
  const handleColumnDragStart = (index: number) => {
    setDraggedColumn(index);
  };

  const handleColumnDragOver = (index: number, e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleColumnDrop = (index: number) => {
    if (draggedColumn !== null) {
      const newOrder = [...nonStickyColumns];
      const [movedColumn] = newOrder.splice(draggedColumn, 1);
      newOrder.splice(index, 0, movedColumn);

      setNonStickyColumns(newOrder);
      setDraggedColumn(null);
    }
  };

  // Row Drag Handlers
  const handleRowDragStart = (index: number) => {
    setDraggedRow(index);
  };

  const handleRowDragOver = (index: number, e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleRowDrop = (index: number) => {
    if (draggedRow !== null && !isLoading) {
      const newOrder = [...tableRows];
      const [movedRow] = newOrder.splice(draggedRow, 1);
      newOrder.splice(index, 0, movedRow);
      setTableRows(newOrder);
      setDraggedRow(null);
    }
  };

  const sortTableRows = () => {
    if (!isLoading) {
      const activeSortColumn = sortingColumns.find(
        (col) => col.sortType !== "default"
      );
      if (!activeSortColumn) return rows;

      const { field, sortType } = activeSortColumn;
      const sortableData = [...rows].sort((a, b) => {
        const aField = a[field];
        const bField = b[field];

        const aValue =
          typeof aField === "string"
            ? aField.replace(/\s/g, "").toLowerCase()
            : aField;
        const bValue =
          typeof bField === "string"
            ? bField.replace(/\s/g, "").toLowerCase()
            : bField;

        if (sortType === "asc") return aValue > bValue ? 1 : -1;
        if (sortType === "desc") return aValue < bValue ? 1 : -1;
        return 0;
      });
      return sortableData;
    }
    return [];
  };

  useEffect(() => {
    if (!isLoading) {
      setTableRows(sortTableRows());
    }
  }, [sortingColumns, rows, isLoading]);

  useEffect(() => {
    if (!isRowSelectable) return;
    if (
      leftStickyColumns.length > 0 &&
      !leftStickyColumns.some((col) => col.field === "selection")
    ) {
      setLeftStickyColumns([selectionColumn, ...leftStickyColumns]);
    } else if (!leftStickyColumns.some((col) => col.field === "selection")) {
      setNonStickyColumns([selectionColumn, ...nonStickyColumns]);
    }
  }, []);

  const loadingPlaceholder = useMemo(() => {
    return getPlaceholder(isPaginatorVisible, showRowPerPage);
  }, [isPaginatorVisible, showRowPerPage]);

  const filteredData = useMemo(
    () => handleSearch(tableRows, filterText),
    [handleSearch, tableRows, filterText]
  );

  const pagesCounts = useMemo(() => {
    return getNumberOfPages(isLoading, showRowPerPage, filteredData.length);
  }, [showRowPerPage, isLoading, filteredData]);

  const currentTableData = useMemo(() => {
    if (!isPaginatorVisible) return filteredData;
    const firstPageIndex = (currentPage - 1) * showRowPerPage;
    const lastPageIndex = firstPageIndex + showRowPerPage;
    return filteredData?.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, showRowPerPage, isPaginatorVisible, filteredData]);

  if (!isLoading && rows.length < 1) {
    return emptyStateComponent ? (
      <div
        style={{
          height: "100%",
        }}
      >
        {emptyStateComponent}
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        No Data Available
      </div>
    );
  }

  return (
    <>
      <div className={`div-grid-container ${tableContainerClassName}`}>
        <TableHeader
          leftColumns={leftStickyColumns}
          mainColumns={nonStickyColumns}
          rightColumns={rightStickyColumns}
          sortingColumns={sortingColumns}
          setSortingColumns={setSortingColumns}
          mainContentRef={contentRef}
          handleMouseDown={handleMouseDown}
          enableColumnDrag={!resizing?.isActive && isColumnDraggable}
          onColumnDragStart={handleColumnDragStart}
          onColumnDragOver={handleColumnDragOver}
          onColumnDrop={handleColumnDrop}
          selectAll={selectAll}
          deselectAll={deselectAll}
          isAllRowsSelected={
            selectedRowsIds.current?.size > 0 &&
            selectedRowsIds.current.size === topLevelRows.length
          }
          showIntermediateStateOnCheckbox={showIntermediateStateOnCheckbox}
          leftMargin={leftMargin}
          rightMargin={rightMargin}
        />
        <TableBody
          leftColumns={leftStickyColumns}
          mainColumns={nonStickyColumns}
          rightColumns={rightStickyColumns}
          bodyRef={bodyRef}
          stickyCols={stickyCols}
          showPaginator={isPaginatorVisible}
          rows={currentTableData}
          enableRowDrag={!!isRowDraggable}
          onRowDragStart={handleRowDragStart}
          onRowDragOver={handleRowDragOver}
          onRowDrop={handleRowDrop}
          allowRowExpansion={isRowExpandable}
          onRowExpanded={onRowExpanded}
          selectedRowIds={selectedRows}
          selectRow={selectRow}
          deselectRow={deselectRow}
          loading={isLoading}
          leftMargin={leftMargin}
          rightMargin={rightMargin}
          expandArrowIcon={expansionArrowIcon}
          virtualizationSettings={virtualizationSettings}
          loadingPlaceholder={loadingPlaceholder}
          rowKey={rowKey}
        />
        {isPaginatorVisible && (
          <Pagination
            onPageChange={handleChangePage}
            totalCount={pagesCounts}
            currentPage={currentPage}
            showRowPerPage={showRowPerPage}
            handlePerChange={handlePerChange}
            paginatorOptions={rowsPerPageOptions}
          />
        )}
      </div>
      {resizing?.isActive && (
        <div ref={overlayRef} className="resize-overlay" />
      )}
    </>
  );
};

export default BlkBoxTable;
